import { useDispatch, useSelector } from "react-redux";
import { Updater } from "use-immer";

import {
  CustomEventFeType,
  PutCustomEventRequestBodyType,
  PutCustomEventResponseBodyType,
} from "@intelligems/ig-types";
import { combinedCustomEventsKeys, customEventsKeys } from "@src/api/queryKeys";
import { ApiRequest } from "@src/api/types/api";
import {
  resetNewCustomEvent,
  selectedCustomEvents,
} from "@src/store/slices/customEvents";
import { closeDrawer } from "@src/store/slices/drawers";
import {
  setCustomMetrics,
  updateCustomMetricEvent,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { setIgnoreUpdateDetected } from "@src/store/slices/global";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

const updateEventsCache = (
  queryClient: any,
  cacheKey: any,
  updatedEvent: any
) => {
  // Update specific event if it exists
  queryClient.setQueryData(cacheKey.one(updatedEvent.id), {
    data: updatedEvent,
  });

  // Update the list data
  queryClient.setQueryData(cacheKey.all, (oldData: any) => {
    if (!oldData) return oldData;
    if (Array.isArray(oldData)) {
      return oldData.map((event: any) =>
        event.id === updatedEvent.id ? updatedEvent : event
      );
    }
    return oldData;
  });
};

const putCustomEvent = async (
  data: ApiRequest<PutCustomEventRequestBodyType>
) => {
  const resp = await api.put<PutCustomEventResponseBodyType>(
    `/v2/custom-events`,
    {
      ...data.body,
    }
  );
  return resp.data;
};

export const usePutCustomEvent = ({
  setSubpageSelected,
  setIsCreating,
  setSelectedGeneric,
  renderLocation,
  selectedCustomEventIds = [],
  dontAddAsSelected,
  setDrawerPage,
}: {
  setSubpageSelected?: Updater<CustomEventFeType | null>;
  setIsCreating?: Updater<boolean>;
  setSelectedGeneric?: Updater<string[]>;
  renderLocation: "eventsManager" | "customMetric" | "keyMetric" | "copyEvent";
  selectedCustomEventIds?: string[];
  dontAddAsSelected?: boolean;
  setDrawerPage?: Updater<string>;
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const customEvents = useSelector(selectedCustomEvents);

  return useMutation<
    PutCustomEventResponseBodyType,
    Error,
    ApiRequest<PutCustomEventRequestBodyType>
  >({
    mutationFn: putCustomEvent,
    mutationKey: ["put-custom-event"],
    onMutate: () => dispatch(setIgnoreUpdateDetected(true)),
    onSuccess: (resp, variables) => {
      const isNewEvent = !variables?.body?.customEvent.id;
      const returnedEvent = resp.customEvent;

      void queryClient.invalidateQueries({ queryKey: customEventsKeys.all });

      if (isNewEvent) {
        void queryClient.invalidateQueries({
          queryKey: combinedCustomEventsKeys.all,
        });
      } else {
        updateEventsCache(queryClient, combinedCustomEventsKeys, returnedEvent);
      }

      if (renderLocation === "copyEvent") {
        dispatch(resetNewCustomEvent());
        return;
      }

      if (renderLocation === "eventsManager") {
        setIsCreating?.(true);
        setDrawerPage?.("subpage");
        dispatch(closeDrawer("customEvents"));
      } else {
        setIsCreating?.(false);
        setDrawerPage?.("list");

        const updatedCustomEvents = [...customEvents, returnedEvent];
        let updatedSelectedIds = selectedCustomEventIds;

        if (
          (renderLocation === "keyMetric" ||
            renderLocation === "customMetric") &&
          setSelectedGeneric
        ) {
          updatedSelectedIds = [...selectedCustomEventIds, returnedEvent.id];
          setSelectedGeneric(updatedSelectedIds);
        } else if (!dontAddAsSelected) {
          updatedSelectedIds = [...selectedCustomEventIds, returnedEvent.id];
        }

        dispatch(
          setCustomMetrics({
            selectedCustomEventIds: updatedSelectedIds,
            allCustomEvents: updatedCustomEvents,
          })
        );
        dispatch(updateCustomMetricEvent({ customEvent: returnedEvent }));
      }

      setSubpageSelected?.(null);
      dispatch(resetNewCustomEvent());
    },
  });
};
